import type { ApolloClient } from '@apollo/client';
import {
  getApolloClient,
  getInitialState,
  rclient,
  setInitialState,
} from '@landsbankinn/prismic';
import type { GetStaticPropsContext } from 'next';
import { navigationQuery } from '../graphql/prismic/navigationQuery';
import { websiteQuery } from '../graphql/prismic/websiteQuery';
import { Config } from './config';
import { allPages } from './linkResolver';
import data from './prismicIntrospect.json';

function extractNavigationItemIds(website: any) {
  return website?.navigation
    ?.map((item: any) => {
      return item?.navigation_item?._meta?.id;
    })
    .filter((n: any) => !!n);
}

function mergeWebsiteNavigations(website: any, navigations: any) {
  return {
    ...website,
    navigation: website.navigation.map((item: any) => ({
      ...item,
      navigation_item: {
        ...item.navigation_item,
        ...navigations.find(
          (n: any) => n?._meta?.id === item?.navigation_item?._meta?.id,
        ),
      },
    })),
  };
}

// this query is split to bypass the maximum 9 level query limit
// @reference https://prismic.io/docs/graphql/query-the-api/fetch-linked-document-fields
async function getWebsiteData(client: ApolloClient<any>, lang = 'is') {
  if (Config.REDIS_URL !== '') {
    const websiteData = await rclient.get(`WEBSITE_DATA_${lang}`);
    if (websiteData) {
      return JSON.parse(websiteData);
    }
  }

  // fetch website
  const websiteRes = await client.query({
    query: websiteQuery,
    variables: { lang },
  });
  const [websiteEdge] = websiteRes.data?.allWebsites?.edges ?? [];
  const website = websiteEdge?.node;
  // extract wanted navigation IDs
  const ids = extractNavigationItemIds(website);
  // fetch navigations
  const navigationRes = await client.query({
    query: navigationQuery,
    variables: {
      ids,
      lang,
    },
  });
  const navigations = (navigationRes.data?.allNavigations?.edges ?? []).map(
    (n: any) => n.node,
  );
  // merge navigations into website navigation items
  const response = mergeWebsiteNavigations(website, navigations);

  // set redis cache
  if (Config.REDIS_URL !== '') {
    rclient.set(`WEBSITE_DATA_${lang}`, JSON.stringify(response), 60 * 2);
  }

  return response;
}

export async function getStaticPropsData(
  props?: GetStaticPropsContext & { prismicRef?: string },
  overwriteLang?: string,
) {
  const { preview = null, previewData = null } = props || {};
  const [lang] = ([] as any).concat(
    overwriteLang?.toLocaleLowerCase() ?? props?.params?.lang ?? 'is',
  );

  const client = getApolloClient({
    // @development state
    introspectionQueryResultData: data,
    initialState: await getInitialState(),
    prismicRef: preview ? (previewData as any)?.ref : null,
  });

  // fetch all pages data
  const pages = await allPages(lang, (previewData as any)?.ref);

  // fetch header and footer data
  const website = await getWebsiteData(client, lang);

  // @development state
  await setInitialState(client.cache);

  // no need to push cache for pages and website
  client.cache.reset();

  function extractProps() {
    return {
      lang,
      pages,
      website,
      preview,
      previewData,
      initialApolloState: client.cache.extract(),
    };
  }

  function withStaticData(data: any = {}, revalidate?: number) {
    return {
      props: {
        ...data,
        ...extractProps(),
      },
      revalidate: revalidate ?? Config.REVALIDATION_TIMEOUT,
    };
  }

  return {
    client,
    pages,
    website,
    withStaticData,
    extractProps,
  };
}

export function getStaticPropsDefaults({ lang = 'is' }: any = {}) {
  return async function getStaticProps(props: GetStaticPropsContext) {
    props.params = props.params || {};
    props.params.lang = lang;
    const { extractProps } = await getStaticPropsData(props);
    return {
      props: extractProps(),
      revalidate: Config.REVALIDATION_TIMEOUT,
    };
  };
}
